import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import tokenStorage from "../../../../services/tokenStorage";

import {
    Box,
    Card,
    CardContent,
    CardHeader,
    CardMedia,
    MenuItem,
    Link,
    ListItemText,
    Typography,
} from "@mui/material";
import AlertMessage from "../../../Common/Alert/AlertMessage";
import BlankCard from "../../../Common/Card/BlankCard";
import CENTAvatar from "../CENTAvatar";
import ListItemMenu from "../../../Common/ListItemMenu";
import RenderDate from "../../../Common/Date/RenderDate";

import { activateApplication } from "../../../../actions/oauthActions";
import { getApplication, isAppInactive } from "../../../../reducers";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

const SubscriptionCard = ({ data }) => {
    const dispatch = useDispatch();
    const application = useSelector((state) =>
        getApplication(state, data.service.appCode)
    );

    const isInactive = useSelector((state) =>
        isAppInactive(state, data.service.appCode, data.service.uuid)
    );

    const [link, setLink] = useState(data.service.baseUrl);

    if (!data.uuid) return <BlankCard />;

    const activateApp = () =>
        dispatch(activateApplication(data.service.appCode, data.service.uuid));

    if (!!application && data?.service?.appCode.startsWith("P-")) {
        tokenStorage.getSSOToken(application.uuid).then((token) => {
            if (!!token) {
                setLink(`${data.service.baseUrl}/sso?access_token=${token}`);
            }
        });
    }

    const expired = dayjs().isAfter(data.dateEnd, "day");
    const healthy = dayjs().diff(data.dateEnd, "month", true) < -1;

    let alertSeverity = "info";
    let avatarBadge = isInactive ? "" : "checked";
    let alertMessage =
        (expired ? "Expired " : "Expires ") + dayjs().to(dayjs(data.dateEnd));

    if (healthy) {
        alertSeverity = "success";
        alertMessage = "Subscribed";
    }

    if (data.status !== "active") {
        avatarBadge = "warning";
        alertSeverity = "error";
        alertMessage =
            "There is a problem with this subscription, please contact support.";
    }

    if (expired) {
        avatarBadge = "error";
        alertSeverity = "error";
    }

    if (!data.service.token) {
        avatarBadge = "warning";
        alertSeverity = "error";
        alertMessage =
            "This subscription does not have a valid token, please contact support.";
    }

    let media =
        "https://cdn.premierit.com/images/banners/cent/bnr-sub_blank.png";

    switch (data.service.appCode) {
        case "P-JP":
            media =
                "https://cdn.premierit.com/images/banners/jp/bnr-sub_pjp_001.png";
            break;

        case "P-RMSD":
            media =
                "https://cdn.premierit.com/images/banners/rms/bnr-sub_rmsd_001.png";
            break;

        case "P-RMSN":
            media =
                "https://cdn.premierit.com/images/banners/rms/bnr-sub_rmsn_001.png";
            break;

        case "E-MSF":
            media =
                "https://cdn.premierit.com/images/banners/msf/bnr-sub_emsf_001.png";
            break;

        case "P-MSF":
            media =
                "https://cdn.premierit.com/images/banners/msf/bnr-sub_pmsf_001.png";
            break;

        case "C-MSF":
            media =
                "https://cdn.premierit.com/images/banners/msf/bnr-sub_cmsf_001.png";
            break;

        case "C-PORT":
        case "P-PORT":
            media =
                "https://cdn.premierit.com/images/banners/portfolio/bnr-sub_port_001.png";
            break;

        case "C-TMS":
            media =
                "https://cdn.premierit.com/images/banners/tms/bnr-sub_tms_001.png";
            break;

        case "C-LRS":
            media =
                "https://cdn.premierit.com/images/banners/lrs/bnr-sub_clrs_001.png";
            break;

        case "C-LMS":
            media =
                "https://cdn.premierit.com/images/banners/lms/bnr-sub_lms_001.png";
            break;

        case "Z-RS":
            media =
                "https://cdn.premierit.com/images/banners/rs/bnr-sub_zrs_001.png";
            break;

        case "Z-JP":
            media =
                "https://cdn.premierit.com/images/banners/jp/bnr-sub_zjp_001.png";
            break;

        case "X-E360":
            media =
                "https://cdn.premierit.com/images/banners/external/x360/bnr-sub_x360_001.png";
            break;

        default:
            break;
    }

    return (
        <Card className="card">
            <CardMedia component="img" height="194" image={media} alt="" />
            <CardHeader
                disableTypography={true}
                avatar={<CENTAvatar type="subscription" badge={avatarBadge} />}
                action={
                    !!isInactive && (
                        <ListItemMenu>
                            <MenuItem onClick={activateApp}>
                                <ListItemText sx={{ minWidth: "140px" }}>
                                    Activate
                                </ListItemText>
                            </MenuItem>
                        </ListItemMenu>
                    )
                }
                title={
                    <Typography component="h2" className="card-header-title">
                        {data.service.appName}
                    </Typography>
                }
                subheader={
                    <Typography
                        component="span"
                        className="card-header-subtitle"
                    >
                        {data.service.label}
                    </Typography>
                }
            />
            <CardContent>
                <Box className="card-content">
                    <ul className="list">
                        <li>Licence&nbsp;&nbsp;&nbsp;&nbsp;{data.uuid}</li>
                        <li>
                            {!link ? (
                                data.service.baseUrl
                            ) : (
                                <Link href={link} target="_blank">
                                    {data.service.baseUrl}
                                </Link>
                            )}
                        </li>
                        <li>
                            <RenderDate date={data.dateStart} />
                            &nbsp;-&nbsp;
                            <RenderDate date={data.dateEnd} />
                        </li>
                    </ul>
                </Box>
            </CardContent>
            <CardContent>
                <Box className="card-content">
                    <AlertMessage
                        severity={alertSeverity}
                        message={alertMessage}
                    />
                </Box>
            </CardContent>
        </Card>
    );
};

export default SubscriptionCard;
